import React, { useEffect, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import {
	Routes,
	Route,
	BrowserRouter,
	Navigate,
	useNavigate,
} from 'react-router-dom';
import Auth from 'views/auth/Auth';
import Recover from 'views/recover/Recover';
import Layout from 'components/layouts/main/Layout';
import Inventory from 'views/inventory/Inventory';
import Dashboard from 'views/dashboard/Dashboard';
import DetailsInventory from 'views/inventory/details/DetailsInventory';
import Ozonio from 'views/ozonios/Ozonio';
import Ozocio from 'views/ozocios/Ozocio';
import Devices from 'views/devices/Devices';
import DetailsOzonio from 'views/ozonios/DetailsOzonio';
import { getAllAccess, getUser } from 'localStorage/authStorage';
import Solicitudes from 'views/solicitudes/Solicitudes';
import Conversations from 'views/conversations/Conversations';
import Whitelists from 'views/whitelists/Whitelists';
import DetailsOzoner from 'views/ozoner/details/DetailsOzoner';
import DetailsOzocio from 'views/ozocios/DetailsOzocio';
import MobileLayout from 'components/layouts/mobile/MobileLayout';
import { useDevice } from 'design_system/src';
import Notifications from 'views/notifications/Notifications';
import Loans from 'views/loan/Loan';
import { Roles, rolesExcluding } from 'models/ozonio/enums/ozonio.enum';
import { OzonerContextProvider } from 'views/ozoner/details/OzonerContext';
import { RepossessionContextProvider } from 'views/repossessions/RepossessionsContext';
import { Repossessions } from 'views/repossessions/Repossessions';
import { RouteDetail } from 'views/repossessions/routes/RouteDetail';
import { RoutesMobile } from 'views/routesMobile/RoutesMobile';
import { RepossessionMobileContextProvider } from 'views/routesMobile/RepossessionsMobileContext';
import { RouteDetailMobile } from 'views/routesMobile/RouteDetailMobile';
import { RoutesFinalReportMobile } from 'views/routesMobile/RoutesFinalReportMobile';
import { DevicesMapContextProvider } from 'views/devicesMap/DevicesMapContext';
import { DevicesMap } from 'views/devicesMap/DevicesMap';
import { CompanyRole } from 'models/companies/enums/companies.enums';
import ExternalVehicles from 'views/_external/external-vehicles/ExternalVehicles';
import { GeotileMapContextProvider } from 'views/_external/geotileMap/GeotileMapContext';
import { GeotileMap } from 'views/_external/geotileMap/GeotileMap';
import SkuList from 'views/sku/SkuList';
import SkuDetails from 'views/sku-details/SkuDetails';
import Leads from 'views/leads/Leads';

const MOBILE_ROUTES_ROLES = [
	Roles.ADMIN,
	Roles.RECUPERATOR_MANAGER,
	Roles.RECUPERATOR_AGENT,
];

function RequireAuth({
	type,
	children,
}: {
	type: CompanyRole;
	children: JSX.Element;
}) {
	if (!getAllAccess()) {
		if (type === CompanyRole.ADMIN) return <Navigate to="/auth" />;
		return <Navigate to="/auth-sauron" />;
	}
	return children;
}
function RequireDevice({
	mobile,
	children,
}: {
	mobile: boolean;
	children: JSX.Element;
}) {
	const user = getUser();
	const { isMobile } = useDevice();
	const navigate = useNavigate();
	const [firstUpdate, setFirstUpdate] = useState(true);

	useEffect(() => {
		if (firstUpdate) {
			setFirstUpdate(false);
		} else if (!!mobile !== !!isMobile) {
			const canUseMobileRoutes = MOBILE_ROUTES_ROLES.some((role) =>
				user?.role.includes(role),
			);
			if (isMobile && canUseMobileRoutes) navigate('/routes');
			else if (!isMobile) navigate('/');
		}
	}, [isMobile]);

	return children;
}
function RequireRole({
	children,
	authRoles,
}: {
	children: JSX.Element;
	authRoles: Roles[] | undefined;
}) {
	const user = getUser();
	if (
		authRoles?.length &&
		(!user || !authRoles.some((role) => user.role.includes(role)))
	) {
		return <Navigate to="/" />;
	}
	return children;
}
function RequireCompany({
	children,
	company,
}: {
	children: JSX.Element;
	company: CompanyRole;
}) {
	const user = getUser();
	if (!user || user.company?.role !== company) {
		return OpenDefault();
	}
	return children;
}

function OpenDefault() {
	const user = getUser();
	if (user?.company?.role === CompanyRole.ADMIN) {
		return <Navigate to="/" />;
	}
	if (user?.company?.role === CompanyRole.CLIENT) {
		return <Navigate to="/external/vehicles" />;
	}
	return <Navigate to="/auth" />;
}

const layoutRoutes = [
	{
		path: '/',
		element: <Dashboard />,
	},
	{
		path: '/loans',
		element: <Loans />,
	},
	{
		path: '/solicitudes',
		element: <Solicitudes />,
	},
	{
		path: '/conversaciones',
		element: <Conversations />,
	},
	{
		path: '/listas-blancas',
		element: <Whitelists />,
		roles: rolesExcluding(Roles.SELLER),
	},
	{
		path: '/leads',
		element: <Leads />,
		roles: [Roles.ADMIN, Roles.CREDIT_ANALYST, Roles.SELLER],
	},
	{
		path: '/inventory',
		element: <Inventory />,
		roles: rolesExcluding(Roles.SELLER),
	},
	{
		path: '/inventory/:internalid',
		element: <DetailsInventory />,
		roles: rolesExcluding(Roles.SELLER),
	},
	{
		path: '/sku',
		element: <SkuList />,
		roles: [Roles.ADMIN, Roles.INVENTORY_MODEL_MANAGER],
	},
	{
		path: '/sku/:code',
		element: <SkuDetails />,
		roles: [Roles.ADMIN, Roles.INVENTORY_MODEL_MANAGER],
	},
	{
		path: '/ozocio',
		element: <Ozocio />,
		roles: [Roles.ADMIN, Roles.BUYER_MANAGER],
	},
	{
		path: '/ozocio/:_id',
		element: <DetailsOzocio />,
		roles: [Roles.ADMIN, Roles.BUYER_MANAGER],
	},
	{
		path: '/ozoner/:_id',
		element: (
			<OzonerContextProvider>
				<DetailsOzoner />
			</OzonerContextProvider>
		),
	},
	{
		path: '/ozonio',
		element: <Ozonio />,
		roles: [Roles.ADMIN, Roles.USER_MANAGMENT],
	},
	{
		path: '/ozonio/:_id',
		element: <DetailsOzonio />,
		roles: [Roles.ADMIN, Roles.USER_MANAGMENT],
	},
	{
		path: '/recovery',
		element: (
			<RepossessionContextProvider>
				<Repossessions />
			</RepossessionContextProvider>
		),
		roles: [Roles.ADMIN, Roles.RECUPERATOR_MANAGER],
		isMobile: false,
	},
	{
		path: '/gps-map',
		element: (
			<DevicesMapContextProvider>
				<DevicesMap />
			</DevicesMapContextProvider>
		),
		roles: [Roles.ADMIN, Roles.GPS_VIEWER, Roles.GPS_MANAGER],
		isMobile: false,
	},
	{
		path: '/route/:_id',
		element: <RouteDetail />,
		roles: [Roles.ADMIN, Roles.RECUPERATOR_MANAGER],
	},
	{
		path: '/notifications',
		element: <Notifications />,
	},
	{
		path: '/devices',
		element: <Devices />,
		roles: [Roles.ADMIN, Roles.DEVICES_MANAGER],
	},
	{
		path: '/routes',
		element: (
			<RepossessionMobileContextProvider>
				<RoutesMobile />
			</RepossessionMobileContextProvider>
		),
		isMobile: true,
		roles: MOBILE_ROUTES_ROLES,
	},
	{
		path: '/routes/:_id',
		element: (
			<RepossessionMobileContextProvider>
				<RouteDetailMobile />
			</RepossessionMobileContextProvider>
		),
		isMobile: true,
		roles: MOBILE_ROUTES_ROLES,
	},
	{
		path: '/routes/final-report',
		element: (
			<RepossessionMobileContextProvider>
				<RoutesFinalReportMobile />
			</RepossessionMobileContextProvider>
		),
		isMobile: true,
		roles: MOBILE_ROUTES_ROLES,
	},
];

const layoutExternalRoutes = [
	{
		path: '/external/vehicles',
		element: <ExternalVehicles />,
		roles: [],
	},
	{
		path: '/external/gps-map',
		element: (
			<GeotileMapContextProvider>
				<GeotileMap />
			</GeotileMapContextProvider>
		),
		roles: [],
	},
];

function RootRouter() {
	const { isMobile } = useDevice();

	return (
		<BrowserRouter>
			<CssBaseline />

			<Routes>
				<Route path="auth" element={<Auth type={CompanyRole.ADMIN} />} />
				<Route
					path="auth-sauron"
					element={<Auth type={CompanyRole.CLIENT} />}
				/>
				<Route path="recover" element={<Recover />} />
				<Route
					element={
						<RequireCompany company={CompanyRole.ADMIN}>
							<RequireAuth type={CompanyRole.ADMIN}>
								<RequireDevice mobile={false}>
									<Layout />
								</RequireDevice>
							</RequireAuth>
						</RequireCompany>
					}
				>
					{layoutRoutes
						.filter((route) => !route.isMobile)
						.map((route) => (
							<Route
								key={route.path}
								path={route.path}
								element={
									<RequireRole authRoles={route.roles}>
										{route.element}
									</RequireRole>
								}
							/>
						))}
				</Route>
				<Route
					element={
						<RequireCompany company={CompanyRole.CLIENT}>
							<RequireAuth type={CompanyRole.CLIENT}>
								<Layout />
							</RequireAuth>
						</RequireCompany>
					}
				>
					{layoutExternalRoutes.map((route) => (
						<Route
							key={route.path}
							path={route.path}
							element={
								<RequireRole authRoles={route.roles}>
									{route.element}
								</RequireRole>
							}
						/>
					))}
				</Route>
				<Route
					element={
						<RequireAuth type={CompanyRole.ADMIN}>
							<RequireDevice mobile>
								<MobileLayout />
							</RequireDevice>
						</RequireAuth>
					}
				>
					{layoutRoutes
						.filter((route) => route.isMobile)
						.map((route) => (
							<Route
								key={route.path}
								path={route.path}
								element={
									<RequireRole authRoles={route.roles}>
										{route.element}
									</RequireRole>
								}
							/>
						))}
				</Route>
				<Route path="*" element={<OpenDefault />} />
			</Routes>
		</BrowserRouter>
	);
}
export default RootRouter;
